"use client";

import React, { useState, FormEvent, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import LoadingScreen from "@/components/common/LoadingScreen";

export default function LoginForm() {
  const router = useRouter();

  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/";

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [loginFailed, setLoginFailed] = useState<boolean>(false);

  const [isLoginInProgress, setIsLoginInProgress] = useState<boolean>(false);

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  useEffect(() => {
    sessionStorage.removeItem("selectedElementTypeId");
  }, []);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoginInProgress(true);
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, rememberMe }),
    });

    if (response.ok) {
      await router.replace(redirect);
      router.refresh();
      setIsLoginInProgress(false);
    } else {
      setLoginFailed(true);
      setIsLoginInProgress(false);
    }
  };
  return (
    <form className="space-y-4 md:space-y-6" onSubmit={handleLogin}>
      <div>
        <label htmlFor="username" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Username</label>
        <input type="text"
          value={username}
          className="bg-gray-50 border border-gray-300 text-sm text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          placeholder="Username"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Password</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            placeholder="••••••••"
            className="bg-gray-50 border border-gray-300 text-sm text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-start">
          <div className="flex items-center h-5">
          <label htmlFor="remember" className="flex items-center text-gray-500 dark:text-gray-300 cursor-pointer">
            <input id="remember" aria-describedby="remember" type="checkbox" className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
            <span className="ml-2 text-xs xl:text-sm text-gray-900 dark:text-white">Remember me</span>
            </label>
          </div>
        </div>
        {/* <Link href="/forgot-password" className="text-sm font-medium text-gray-500 dark:text-gray-300 hover:underline">Forgot password?</Link> */}
      </div>
      <button type="submit" className="w-full text-white bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800">Sign in</button>
      <p className="flex justify-center mt-6 text-sm text-gray-600">
        Don&apos;t have an account?
        <Link href="https://bohm.work/" className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-300 hover:underline">
          Contact BOHM
        </Link>
      </p>
      {loginFailed && <p className="text-red-500 text-sm text-center">Login failed. Please check your username and password.</p>}
      {isLoginInProgress && <LoadingScreen message="Logging in..." />}
      {/* <p className="flex justify-center mt-6 text-sm text-gray-600">
          By clicking continue, you agree to our <Link href="/terms" className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-300 hover:underline">Terms of Service</Link> and <Link href="/privacy" className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-300 hover:underline">Privacy Policy</Link>.
        </p> */}
    </form>
  );
}
